import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Plus, ArrowRight } from "lucide-react";
import { FormQuestion, QuestionGroup, QuestionGroupNode } from "@/hooks/useQuestionMappings";

interface AvailableQuestionsProps {
  questions: FormQuestion[];
  groups: QuestionGroup;
  onAddToGroup: (path: string[], questionNames: string[]) => void;
}

interface GroupOption {
  label: string;
  path: string[];
  level: number;
}

function buildGroupOptions(
  groups: QuestionGroup | { [key: string]: QuestionGroupNode },
  parentPath: string[] = [],
  level: number = 0
): GroupOption[] {
  const options: GroupOption[] = [];

  Object.entries(groups).forEach(([groupName, node]) => {
    const currentPath = [...parentPath, groupName];
    
    options.push({
      label: groupName,
      path: currentPath,
      level
    });

    // Add subgroups recursively
    if ('subgroups' in node && node.subgroups && Object.keys(node.subgroups).length > 0) {
      const subOptions = buildGroupOptions(node.subgroups, currentPath, level + 1);
      options.push(...subOptions);
    }
  });

  return options;
}

function extractAssignedQuestions(
  groups: QuestionGroup | { [key: string]: QuestionGroupNode }
): Set<string> {
  const assignedQuestions = new Set<string>();

  console.log('🔍 extractAssignedQuestions DEBUG:');
  console.log('  - Groups input:', groups);

  Object.values(groups).forEach((node) => {
    console.log('  - Processing node:', node);
    
    // Extraer preguntas del nodo actual
    if (node.questions) {
      console.log('  - Node questions:', node.questions);
      node.questions.forEach((questionItem) => {
        const questionName = typeof questionItem === 'string' ? questionItem : questionItem.name;
        console.log(`  - Adding question: ${questionName} (from ${typeof questionItem})`);
        assignedQuestions.add(questionName);
      });
    }

    // Extraer preguntas de subgrupos recursivamente
    if ('subgroups' in node && node.subgroups && Object.keys(node.subgroups).length > 0) {
      console.log('  - Processing subgroups:', node.subgroups);
      const subAssigned = extractAssignedQuestions(node.subgroups);
      subAssigned.forEach(q => {
        console.log(`  - Adding subgroup question: ${q}`);
        assignedQuestions.add(q);
      });
    }
  });

  console.log('  - Final assigned questions:', Array.from(assignedQuestions));
  return assignedQuestions;
}

export function AvailableQuestions({ questions, groups, onAddToGroup }: AvailableQuestionsProps) {
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [targetGroupPath, setTargetGroupPath] = useState<string>("");

  const groupOptions = useMemo(() => buildGroupOptions(groups), [groups]);
  const assignedQuestions = useMemo(() => extractAssignedQuestions(groups), [groups]);

  console.log('🔍 AvailableQuestions DEBUG:');
  console.log('  - Total questions received:', questions.length);
  console.log('  - Assigned questions:', Array.from(assignedQuestions));
  console.log('  - Groups structure:', groups);

  const filteredQuestions = questions.filter(q => {
    // Excluir preguntas ya asignadas
    if (assignedQuestions.has(q.name)) {
      console.log(`  - Filtering out assigned question: ${q.name}`);
      return false;
    }
    
    const name = q.name?.toLowerCase() || '';
    const text = q.text?.toLowerCase() || '';
    const search = searchTerm.toLowerCase();
    return name.includes(search) || text.includes(search);
  });

  console.log('  - Final filtered questions:', filteredQuestions.map(q => q.name));

  const handleToggleQuestion = (questionName: string) => {
    const newSelected = new Set(selectedQuestions);
    if (newSelected.has(questionName)) {
      newSelected.delete(questionName);
    } else {
      newSelected.add(questionName);
    }
    setSelectedQuestions(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedQuestions.size === filteredQuestions.length) {
      setSelectedQuestions(new Set());
    } else {
      setSelectedQuestions(new Set(filteredQuestions.map(q => q.name)));
    }
  };

  const handleAddToGroup = () => {
    if (!targetGroupPath || selectedQuestions.size === 0) return;

    const path = targetGroupPath.split('|||');
    onAddToGroup(path, Array.from(selectedQuestions));
    setSelectedQuestions(new Set());
    setTargetGroupPath("");
  };

  const groupNames = Object.keys(groups);

  return (
    <Card className="h-[calc(100vh-250px)]">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Preguntas Disponibles</span>
          <Badge variant="secondary">{questions.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Buscar pregunta..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Select All */}
        {filteredQuestions.length > 0 && (
          <div className="flex items-center gap-2 pb-2 border-b">
            <Checkbox
              id="select-all"
              checked={selectedQuestions.size === filteredQuestions.length && filteredQuestions.length > 0}
              onCheckedChange={handleSelectAll}
            />
            <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
              Seleccionar todas ({selectedQuestions.size}/{filteredQuestions.length})
            </label>
          </div>
        )}

        {/* Questions List */}
        <div className="space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 500px)' }}>
          {filteredQuestions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? 'No se encontraron preguntas' : 'Todas las preguntas están agrupadas'}
            </div>
          ) : (
            filteredQuestions.map((question) => (
              <div
                key={question.id}
                className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <Checkbox
                  id={question.id}
                  checked={selectedQuestions.has(question.name)}
                  onCheckedChange={() => handleToggleQuestion(question.name)}
                />
                <label htmlFor={question.id} className="flex-1 cursor-pointer">
                  <p className="text-sm font-medium text-gray-700">{question.text || question.name || 'Sin nombre'}</p>
                </label>
              </div>
            ))
          )}
        </div>

        {/* Add to Group Action */}
        {selectedQuestions.size > 0 && (
          <div className="pt-4 border-t space-y-2">
            <p className="text-sm font-medium">
              Agregar {selectedQuestions.size} pregunta(s) a:
            </p>
            <div className="flex gap-2">
              <Select value={targetGroupPath} onValueChange={setTargetGroupPath}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Seleccionar grupo o subgrupo" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {groupOptions.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      No hay grupos creados
                    </div>
                  ) : (
                    groupOptions.map((option, idx) => (
                      <SelectItem 
                        key={idx} 
                        value={option.path.join('|||')}
                        className="font-mono text-sm"
                      >
                        <div className="flex items-center gap-1">
                          {option.level > 0 && (
                            <span className="text-muted-foreground">
                              {'└─'.repeat(option.level)}
                            </span>
                          )}
                          <span>{option.path[option.path.length - 1]}</span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <Button
                onClick={handleAddToGroup}
                disabled={!targetGroupPath}
                className="flex items-center gap-2"
              >
                <ArrowRight size={16} />
                Agregar
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
